import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from 'recharts';
import { FiActivity, FiChevronLeft, FiChevronRight, FiRefreshCw } from 'react-icons/fi';
import Layout from '../components/Layout';
import AnalyticsDateRange from '../components/AnalyticsDateRange';
import { formatSingaporeDateTime } from '../utils/singaporeTime';
import { useAnalyticsChartRange, clipAnalyticsChartData, ANALYTICS_DATE_XAXIS_PROPS } from '../hooks/useAnalyticsChartRange';

const PAGE_SIZE = 10;

const formatDeviceDisplay = (device) => {
    const d = (device || '').trim().toLowerCase();
    if (!d) return '—';
    if (d.includes('mobile') || d.includes('phone') || d.includes('tablet')) return '📲';
    if (d.includes('desktop')) return '💻';
    return device;
};

const StatCard = ({ label, visitors, views }) => (
    <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
        <p className="text-sm font-medium text-gray-500">{label}</p>
        <div className="mt-2 flex gap-6">
            <div>
                <p className="text-2xl font-bold text-blue-700">{visitors ?? 0}</p>
                <p className="text-xs text-gray-500">Visitors</p>
            </div>
            <div>
                <p className="text-2xl font-bold text-green-700">{views ?? 0}</p>
                <p className="text-xs text-gray-500">Views</p>
            </div>
        </div>
    </div>
);

const TablePagination = ({ page, totalPages, onPageChange }) => {
    if (totalPages <= 1) return null;
    return (
        <div className="flex items-center justify-between px-3 py-2 border-t border-gray-200 bg-gray-50 text-sm">
            <span className="text-gray-500">
                Page {page} of {totalPages}
            </span>
            <div className="flex gap-1">
                <button
                    type="button"
                    onClick={() => onPageChange(page - 1)}
                    disabled={page === 1}
                    className={`p-1 rounded ${page === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-600 hover:bg-gray-200'}`}
                    aria-label="Previous page"
                >
                    <FiChevronLeft />
                </button>
                <button
                    type="button"
                    onClick={() => onPageChange(page + 1)}
                    disabled={page === totalPages}
                    className={`p-1 rounded ${page === totalPages ? 'text-gray-300 cursor-not-allowed' : 'text-gray-600 hover:bg-gray-200'}`}
                    aria-label="Next page"
                >
                    <FiChevronRight />
                </button>
            </div>
        </div>
    );
};

const PaginatedDataTable = ({ title, columns, rows, emptyText = 'No data' }) => {
    const [page, setPage] = useState(1);
    const allRows = rows || [];
    const totalPages = Math.max(1, Math.ceil(allRows.length / PAGE_SIZE));

    useEffect(() => {
        setPage(1);
    }, [rows]);

    useEffect(() => {
        if (page > totalPages) setPage(totalPages);
    }, [page, totalPages]);

    const paginatedRows = allRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    return (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden flex flex-col h-full">
            <div className="px-4 py-3 border-b border-gray-200">
                <h2 className="text-base font-semibold text-gray-800">{title}</h2>
            </div>
            <div className="overflow-x-auto flex-1">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            {columns.map((col) => (
                                <th
                                    key={col.key}
                                    className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap"
                                >
                                    {col.label}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                        {!paginatedRows.length ? (
                            <tr>
                                <td colSpan={columns.length} className="px-3 py-6 text-center text-gray-500 text-sm">
                                    {emptyText}
                                </td>
                            </tr>
                        ) : (
                            paginatedRows.map((row, i) => (
                                <tr key={row._id || row.visitorId || row.path || `${page}-${i}`} className="hover:bg-gray-50">
                                    {columns.map((col) => (
                                        <td key={col.key} className="px-3 py-2 text-sm text-gray-800">
                                            {col.render ? col.render(row) : row[col.key] ?? '—'}
                                        </td>
                                    ))}
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
            <TablePagination page={page} totalPages={totalPages} onPageChange={setPage} />
        </div>
    );
};

const SimonAnalyticsPage = () => {
    const [dashboard, setDashboard] = useState(null);
    const [chartData, setChartData] = useState([]);
    const [clickChart, setClickChart] = useState({ data: [], lines: [] });
    const [chartTab, setChartTab] = useState('traffic');
    const { chartFrom, setChartFrom, chartTo, setChartTo, rangeReady } = useAnalyticsChartRange();
    const [loading, setLoading] = useState(true);
    const [chartLoading, setChartLoading] = useState(true);
    const [clickChartLoading, setClickChartLoading] = useState(true);
    const [error, setError] = useState('');

    const authHeaders = () => ({
        Authorization: `Bearer ${localStorage.getItem('token')}`,
        'ngrok-skip-browser-warning': 'true',
    });

    const fetchDashboard = useCallback(async () => {
        if (!chartFrom || !chartTo) return;
        try {
            setLoading(true);
            setError('');
            const res = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/simon-analytics/dashboard`,
                { headers: authHeaders(), params: { from: chartFrom, to: chartTo } }
            );
            if (res.data.success) setDashboard(res.data.data);
        } catch (err) {
            setError(err.response?.data?.error || err.message || 'Failed to load analytics');
        } finally {
            setLoading(false);
        }
    }, [chartFrom, chartTo]);

    const fetchChart = useCallback(async () => {
        if (!chartFrom || !chartTo) return;
        try {
            setChartLoading(true);
            const res = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/simon-analytics/chart`,
                {
                    headers: authHeaders(),
                    params: { from: chartFrom, to: chartTo },
                }
            );
            if (res.data.success) setChartData(res.data.data || []);
        } catch (err) {
            setError(err.response?.data?.error || err.message || 'Failed to load chart');
        } finally {
            setChartLoading(false);
        }
    }, [chartFrom, chartTo]);

    const fetchClickChart = useCallback(async () => {
        if (!chartFrom || !chartTo) return;
        try {
            setClickChartLoading(true);
            const res = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/simon-analytics/click-chart`,
                {
                    headers: authHeaders(),
                    params: { from: chartFrom, to: chartTo },
                }
            );
            if (res.data.success) {
                setClickChart(res.data.data || { data: [], lines: [] });
            }
        } catch (err) {
            setError(err.response?.data?.error || err.message || 'Failed to load click chart');
        } finally {
            setClickChartLoading(false);
        }
    }, [chartFrom, chartTo]);

    useEffect(() => {
        fetchDashboard();
    }, [fetchDashboard]);

    useEffect(() => {
        if (!rangeReady) return;
        fetchChart();
        fetchClickChart();
    }, [rangeReady, fetchChart, fetchClickChart]);

    const summary = dashboard?.trafficSummary;
    const trafficSeries = clipAnalyticsChartData(chartData, chartFrom, chartTo);
    const clickSeries = clipAnalyticsChartData(clickChart.data, chartFrom, chartTo);

    const activeVisitorColumns = [
        { key: 'path', label: 'Page' },
        { key: 'views', label: 'Views' },
        { key: 'ip', label: 'IP' },
        { key: 'browser', label: 'Browser' },
        { key: 'device', label: 'Device', render: (row) => formatDeviceDisplay(row.device) },
        { key: 'country', label: 'Country' },
        {
            key: 'lastViewedAt',
            label: 'Last seen',
            render: (row) => formatSingaporeDateTime(row.lastViewedAt),
        },
    ];

    const latestVisitorColumns = [
        { key: 'path', label: 'Page' },
        { key: 'ip', label: 'IP' },
        { key: 'browser', label: 'Browser' },
        { key: 'device', label: 'Device', render: (row) => formatDeviceDisplay(row.device) },
        { key: 'country', label: 'Country' },
        {
            key: 'viewedAt',
            label: 'Viewed at',
            render: (row) => formatSingaporeDateTime(row.viewedAt),
        },
    ];

    return (
        <Layout>
            <div className="px-2 w-full max-w-none p-6 space-y-6">
                <div className="flex flex-wrap items-center justify-between gap-4">
                    <div className="flex items-center gap-2">
                        <FiActivity className="h-6 w-6 text-cyan-600" />
                        <h1 className="text-2xl font-bold">Simon Analytics</h1>
                    </div>
                    <div className="flex flex-wrap items-end gap-3">
                        <AnalyticsDateRange
                            chartFrom={chartFrom}
                            setChartFrom={setChartFrom}
                            chartTo={chartTo}
                            setChartTo={setChartTo}
                        />
                        <button
                            type="button"
                            onClick={() => {
                                fetchDashboard();
                                fetchChart();
                                fetchClickChart();
                            }}
                            className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-gray-100 rounded-md hover:bg-gray-200"
                        >
                            <FiRefreshCw className="w-4 h-4" />
                            Refresh
                        </button>
                    </div>
                </div>

                {error && (
                    <div className="p-4 bg-red-100 text-red-700 rounded-md">{error}</div>
                )}

                <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
                    <div className="flex flex-wrap gap-2 border-b border-gray-200 mb-4">
                        <button
                            type="button"
                            onClick={() => setChartTab('traffic')}
                            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                                chartTab === 'traffic'
                                    ? 'border-cyan-600 text-cyan-700'
                                    : 'border-transparent text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            Visitors &amp; views over time
                        </button>
                        <button
                            type="button"
                            onClick={() => setChartTab('clicks')}
                            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                                chartTab === 'clicks'
                                    ? 'border-cyan-600 text-cyan-700'
                                    : 'border-transparent text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            Click rate over time
                        </button>
                    </div>

                    {chartTab === 'traffic' ? (
                        chartLoading ? (
                            <p className="text-gray-500 py-12 text-center">Loading chart...</p>
                        ) : trafficSeries.length === 0 ? (
                            <p className="text-gray-500 py-12 text-center">No data for selected range</p>
                        ) : (
                            <ResponsiveContainer width="100%" height={320}>
                                <LineChart key={`traffic-${chartFrom}-${chartTo}`} data={trafficSeries}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis {...ANALYTICS_DATE_XAXIS_PROPS} />
                                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                                    <Tooltip />
                                    <Legend />
                                    <Line
                                        type="monotone"
                                        dataKey="visitors"
                                        name="Visitors"
                                        stroke="#0891b2"
                                        strokeWidth={2}
                                        dot={false}
                                    />
                                    <Line
                                        type="monotone"
                                        dataKey="views"
                                        name="Views"
                                        stroke="#16a34a"
                                        strokeWidth={2}
                                        dot={false}
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        )
                    ) : clickChartLoading ? (
                        <p className="text-gray-500 py-12 text-center">Loading click chart...</p>
                    ) : !clickSeries.length || !clickChart.lines?.length ? (
                        <p className="text-gray-500 py-12 text-center">No click data for selected range</p>
                    ) : (
                        <ResponsiveContainer width="100%" height={360}>
                            <LineChart key={`clicks-${chartFrom}-${chartTo}`} data={clickSeries}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis {...ANALYTICS_DATE_XAXIS_PROPS} />
                                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} label={{ value: 'Clicks', angle: -90, position: 'insideLeft', style: { fontSize: 11 } }} />
                                <Tooltip />
                                <Legend wrapperStyle={{ fontSize: '11px' }} />
                                {(clickChart.lines || []).map((line) => (
                                    <Line
                                        key={line.key}
                                        type="monotone"
                                        dataKey={line.key}
                                        name={line.name}
                                        stroke={line.color}
                                        strokeWidth={2}
                                        dot={false}
                                    />
                                ))}
                            </LineChart>
                        </ResponsiveContainer>
                    )}
                </div>

                {loading ? (
                    <p className="text-gray-600">Loading dashboard...</p>
                ) : dashboard ? (
                    <>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
                            <div className="bg-cyan-50 rounded-lg border border-cyan-200 p-4">
                                <p className="text-sm font-medium text-cyan-800">Currently online</p>
                                <p className="text-3xl font-bold text-cyan-700 mt-1">
                                    {dashboard.onlineNow ?? 0}
                                </p>
                                <p className="text-xs text-cyan-600 mt-1">Last 15 minutes</p>
                            </div>
                            <StatCard label="Today" {...summary?.today} />
                            <StatCard label="Yesterday" {...summary?.yesterday} />
                            <StatCard label="Last 7 days" {...summary?.last7Days} />
                            <StatCard label="Last 30 days" {...summary?.last30Days} />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                            <PaginatedDataTable
                                title="Referrals from search engines"
                                columns={[
                                    { key: 'searchEngine', label: 'Search engine' },
                                    { key: 'visits', label: 'Visits' },
                                ]}
                                rows={dashboard.searchReferrals}
                            />
                            <PaginatedDataTable
                                title="Top pages"
                                columns={[
                                    { key: 'path', label: 'Page' },
                                    { key: 'views', label: 'Views' },
                                    { key: 'uniqueVisitors', label: 'Visitors' },
                                ]}
                                rows={dashboard.topPages}
                            />
                            <PaginatedDataTable
                                title="Traffic sources — UTM"
                                columns={[
                                    { key: 'utmSource', label: 'Source' },
                                    { key: 'utmMedium', label: 'Medium' },
                                    { key: 'utmCampaign', label: 'Campaign' },
                                    { key: 'visits', label: 'Visits' },
                                ]}
                                rows={dashboard.utmSources}
                            />
                            <PaginatedDataTable
                                title="Top referring sites"
                                columns={[
                                    { key: 'referrerDomain', label: 'Site' },
                                    { key: 'visits', label: 'Visits' },
                                    { key: 'uniqueVisitors', label: 'Visitors' },
                                ]}
                                rows={dashboard.topReferrers}
                            />
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            <PaginatedDataTable
                                title="Active visitors"
                                columns={activeVisitorColumns}
                                rows={dashboard.activeVisitors}
                            />
                            <PaginatedDataTable
                                title="Latest visitors"
                                columns={latestVisitorColumns}
                                rows={dashboard.latestVisitors}
                            />
                        </div>
                    </>
                ) : null}
            </div>
        </Layout>
    );
};

export default SimonAnalyticsPage;
